import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSettings, useGlobalModules } from 'contexts';
import getShopifyData from 'lib/shopify/getShopifyData';
import useCollectionSelectItemTracking from 'hooks/tracking/useCollectionSelectItemTracking';
import { useDiscountFreeItem, useDidMountEffect } from 'hooks';
import announce from 'lib/utils/announce';
import { isProductEligibleForFreeItem, getFreeItemLabelText } from 'hooks/shop/useDiscountFreeItem';
import { reformatLocaleToContentfulLocale } from 'lib/utils/helpers';
import classNames from 'classnames';
import styles from './highlightedProducts.module.scss';
import Slider from '../../shared/slider';
import ProductCard from '../../shared/productCard';

const HighlightedProducts = ({ data, locale }) => {
  const { title, categories, products, anchorId } = data || {};
  const globalModules = useGlobalModules() || {};
  const { translations, settings } = globalModules;
  const [items, setItems] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const { setSaveItemSelectionList } = useSettings();

  const announceItemsRef = useRef(false);
  useDidMountEffect(() => {
    announceItemsRef.current = true;
  }, [currentCategory]);
  useEffect(() => {
    if (!announceItemsRef.current) return;
    announceItemsRef.current = false;
    announce(`${items.length} ${translations?.accessibility?.resultsShown || 'products shown'}`);
  }, [items, translations]);

  // Free Item eligibility
  const hasDiscountConfig = (settings?.freeItem?.discountIDs || []).length > 0;
  const { qualifyingProductIds, qualifyingCollectionIds, discounts } = useDiscountFreeItem(
    settings?.freeItem?.discountIDs || [],
    locale
  );

  const getFreeItemLabel = useCallback(
    product => {
      if (hasDiscountConfig && !discounts) return undefined;

      const productId = `gid://shopify/Product/${product.shopifyData?.variants?.[0]?.product_id}`;
      const productCollectionIds = product.cardShopifyData?.collectionIds || [];
      const isEligible = isProductEligibleForFreeItem(
        productId,
        productCollectionIds,
        qualifyingProductIds,
        qualifyingCollectionIds
      );
      return isEligible ? getFreeItemLabelText(translations) : null;
    },
    [qualifyingProductIds, qualifyingCollectionIds, translations, hasDiscountConfig, discounts]
  );

  const categoryValues = useMemo(() => {
    if (!categories?.length) return null;
    return new Set(categories.map(cat => cat?.value || cat));
  }, [categories]);

  const isMainCategory = useCallback(
    spec => {
      if (categoryValues) {
        return categoryValues.has(spec.value);
      }
      return spec.name?.startsWith('Category');
    },
    [categoryValues]
  );

  const allCategories = useMemo(() => {
    if (categories?.length) return null;
    const extractedCategories = products.reduce((acc, product) => {
      let categorySpecs = [];
      if (product.productSpecs) {
        categorySpecs = product.productSpecs
          .filter(spec => isMainCategory(spec))
          .map(spec => spec.value);
      }
      return [...acc, ...categorySpecs];
    }, []);

    const uniqueCategories = [...new Set(extractedCategories)];
    return uniqueCategories.sort((a, b) => a.localeCompare(b));
  }, [products, categories, isMainCategory]);

  const firstTenProduts = useMemo(() => {
    const contentfulLocale = reformatLocaleToContentfulLocale(locale);
    const filteredProducts = products.filter(product => {
      if (product.shopifyData?.variants?.[0]?.outOfStock) return false;
      if (product.shopifyData?.tags?.includes('free-item')) return false;
      if (!product.availableLocales?.includes(contentfulLocale)) return false;

      if (!currentCategory) return true;
      if (product.productSpecs) {
        return product.productSpecs.some(spec => spec.value === currentCategory);
      }
      return false;
    });

    return filteredProducts.slice(0, 10);
  }, [products, currentCategory, locale]);

  useEffect(() => {
    const updateProductData = (product, shopifyData) => {
      const updatedShopifyData = shopifyData?.find(
        ({ productSKU }) => productSKU === product?.shopifyData?.variants?.[0]?.sku
      );
      if (product.shopifyData?.variants?.[0]) {
        // eslint-disable-next-line no-param-reassign
        product.shopifyData.variants[0] = {
          ...product.shopifyData.variants[0],
          ...(updatedShopifyData?.productCompareAtPrice && {
            compareAtPrice: updatedShopifyData?.productCompareAtPrice,
          }),
          outOfStock: updatedShopifyData?.outOfStock,
          ...(updatedShopifyData?.productPrice && { price: updatedShopifyData?.productPrice }),
          ...(updatedShopifyData?.productTitle && { title: updatedShopifyData?.productTitle }),
        };
      }
    };

    const shopifyDataRetrieval = async () => {
      const productIds = firstTenProduts.map(
        highlight => `gid://shopify/Product/${highlight.shopifyData?.variants[0].product_id}`
      );

      if (productIds.length) {
        const pdpShopifyData = await getShopifyData({ productIds, locale });
        if (pdpShopifyData.length > 0) {
          firstTenProduts.forEach(highlight => {
            updateProductData(highlight, pdpShopifyData);
            const cardData = pdpShopifyData.find(
              ({ productSKU }) => highlight.shopifyData?.variants[0]?.sku === productSKU
            );
            // eslint-disable-next-line no-param-reassign
            highlight.cardShopifyData = cardData;
          });
        }

        setItems(firstTenProduts);
      } else {
        setItems(firstTenProduts);
      }
    };
    shopifyDataRetrieval();
  }, [firstTenProduts]);

  const trackSelectItem = (localizedProduct, position) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCollectionSelectItemTracking({
      product: {
        name: localizedProduct.cardShopifyData?.productTitle,
        id: localizedProduct.shopifyData?.variants?.[0]?.product_id,
        category: localizedProduct?.title?.split(' ')?.[0],
        category2: localizedProduct?.title?.slice(-1)?.[0],
        price: localizedProduct.cardShopifyData?.productPrice || '',
        position,
      },
      list: 'Related products',
      setSaveItemSelectionList,
      locale,
    });
  };

  if (!items.length) return null;

  const panelId = `highlighted-products-${(title || 'section').replace(/\s+/g, '-').toLowerCase()}`;

  const Filters = () => {
    const displayCategories = categories?.length ? categories : allCategories;
    if (!displayCategories?.length) return null;

    return (
      <div className={styles.categories} role="tablist" aria-label={title || ''}>
        <button
          type="button"
          role="tab"
          aria-selected={currentCategory === null}
          aria-controls={panelId}
          onClick={() => setCurrentCategory(null)}
          className={classNames(styles.categoryButton, {
            [styles.activeCategory]: currentCategory === null,
          })}
        >
          {translations?.label?.all}
        </button>

        {displayCategories.map(category => {
          const categoryValue = category?.value || category;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={currentCategory === categoryValue}
              aria-controls={panelId}
              key={`category_button_${categoryValue}`}
              onClick={() => setCurrentCategory(categoryValue)}
              className={classNames(styles.categoryButton, {
                [styles.activeCategory]: currentCategory === categoryValue,
              })}
            >
              {categoryValue}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <section id={anchorId} key={title}>
      <Slider
        title={title}
        subtitleContent={<Filters />}
        contentId={panelId}
        contentRole="tabpanel"
      >
        {items?.map((localizedProduct, position) => (
          <ProductCard
            onClick={() => {
              trackSelectItem(localizedProduct, position);
            }}
            locale={locale}
            className={styles.relatedProductsItem}
            itemData={localizedProduct}
            key={localizedProduct.slug}
            itemIndex={position}
            pageTitle="Related Products"
            freeItemLabel={getFreeItemLabel(localizedProduct)}
          />
        ))}
      </Slider>
    </section>
  );
};

export default HighlightedProducts;
